import { settingsWords as words, settingsText } from "./settings-copy";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Box, BoxState, Result } from "@/components/ui/box.schema";
import { NativeSelect } from "@/components/ui/native-select";
import { ApiError } from "@/lib/api/client";
import {
  checkCommit,
  decodeBulkResult,
  reasonOnError,
  type BulkResult,
} from "@/lib/api/commitSeams";
import { facultyApi } from "@/lib/api/faculty";
import {
  zoomAdminApi,
  type RefreshVerification,
  type ZoomConnection,
  type ZoomHostBinding,
} from "@/lib/api/zoom-admin";
import { boxRead, imam } from "./settings-boxes";
import { settingsCopy as copy } from "./settings-copy";

type Existing = { connection: ZoomConnection; host: ZoomHostBinding };
export function useZoomVerificationBox(enabled: boolean): Box {
  const instructors = useQuery({
    queryKey: ["admin", "verification-instructors"],
    queryFn: () => facultyApi.list(),
    enabled,
    retry: false,
  });
  const [userId, setUserId] = useState("");
  const [existing, setExisting] = useState<Existing | null>(null);
  const [phase, setPhase] = useState<
    "idle" | "unread" | "reading" | "known" | "empty" | "failed"
  >("idle");
  const [result, setResult] = useState<Result>({ state: "none" });
  const [pending, setPending] = useState(false);
  const sequence = useRef(0);
  const inspect = async () => {
    // A read can explain a failed item without discarding its original receipt.
    if (result.state !== "partial") setResult({ state: "none" });
    const turn = ++sequence.current;
    const selected = userId;
    setPhase("reading");
    try {
      const [{ connection }, { host_binding: host }] = await Promise.all([
        zoomAdminApi.getConnection(),
        zoomAdminApi.getHostBinding(selected),
      ]);
      if (turn !== sequence.current) return;
      const matches =
        connection &&
        host &&
        !connection.revoked_at &&
        !host.revoked_at &&
        host.user_id === selected &&
        host.account_id === connection.account_id &&
        host.credential_generation === connection.credential_generation &&
        Boolean(host.zoom_user_id.trim());
      setExisting(matches ? { connection, host } : null);
      setPhase(matches ? "known" : "empty");
    } catch {
      if (turn === sequence.current) {
        setExisting(null);
        setPhase("failed");
      }
    }
  };
  const run = async (
    key: string,
    command: RefreshVerification,
    selected: string,
    retry = false,
  ): Promise<Result> => {
    const fromBulk = (bulk: BulkResult): Result =>
      bulk.state === "confirmed"
        ? {
            state: "confirmed",
            at: new Date().toISOString(),
            effects: bulk.succeeded.map((item) => item.reason.message),
            remains: [],
          }
        : {
            state: "partial",
            at: new Date().toISOString(),
            went: bulk.succeeded.map((item) => ({
              ids: [item.id],
              sentence: item.reason.message,
            })),
            failed: bulk.failed.map((item) => ({
              ids: [item.id],
              sentence: item.reason.message,
              fix: "verification",
            })),
            requestId: key,
            retry: {
              verb: words.retryFailedVerification,
              note: words.onlyTheVerificationThatFailedIsSentAgain,
              run: () =>
                run(
                  key,
                  { ...command, ids: bulk.failed.map((item) => item.id) },
                  selected,
                  true,
                ),
            },
          };
    const unknown = (): Result => ({
      state: "unknown",
      cap: "",
      sentence: copy.unknown,
      requestId: key,
      check: {
        verb: words.check,
        note: words.readTheResultOfThisRefresh,
        run: async () => {
          let next: Result;
          try {
            const checked = await checkCommit(key, decodeBulkResult);
            next =
              checked.state === "confirmed" || checked.state === "partial"
                ? fromBulk(checked.receipt.result)
                : checked.state === "not_committed"
                  ? {
                      state: "refused",
                      cap: words.notRefreshed,
                      sentence:
                        words.thisRefreshDidNotGoThroughCheckTheCurrentVerificationBeforeRefreshingAgain,
                      draft: "kept",
                    }
                  : unknown();
          } catch {
            next = unknown();
          }
          setResult(next);
          return next;
        },
      },
    });
    setPending(true);
    let next: Result;
    try {
      next = fromBulk(
        await zoomAdminApi.refreshVerification(selected, command, key, retry),
      );
    } catch (error) {
      next =
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? {
              state: "refused",
              cap: words.notRefreshed,
              sentence: reasonOnError(error)?.message ?? error.message,
              draft: "kept",
            }
          : unknown();
    } finally {
      setPending(false);
    }
    setResult(next);
    return next;
  };
  let state: BoxState = {
    class:
      phase === "known" ? "done" : phase === "failed" ? "failed" : "reading",
    word:
      phase === "known"
        ? words.existingVerificationRead
        : phase === "failed"
          ? words.verificationCouldNotBeRead
          : phase === "empty"
            ? words.noMatchingActiveBinding
            : words.notReadYet,
  };
  if (result.state === "partial" || result.state === "refused")
    state = {
      class: "failed",
      word:
        result.state === "partial"
          ? words.refreshPartlyDone
          : words.refreshRefused,
    };
  if (result.state === "unknown")
    state = { class: "reading", word: words.refreshNotConfirmed };
  return {
    id: "zoom-verification",
    kind: "task",
    label: words.zoomHostVerification,
    job: words.refreshObservationsForAnExistingImmutableZoomHostBinding,
    owner: imam,
    read: boxRead(instructors),
    fold: "never",
    draft: false,
    result,
    state: () => state,
    detail: () =>
      words.checkReadsTheCurrentConnectionAndHostBindingRefreshRecordsANewProviderObservation,
    candidate: { eligible: true, blocked: null, rank: null },
    parts: [
      {
        id: "verification",
        label: words.instructor,
        owner: imam,
        fold: "never",
        draft: "none",
        state: { class: "quiet", word: words.existingIMAMIdentity },
        markedByBox: true,
        render: () => (
          <div className="grid gap-3">
            <NativeSelect
              aria-label={words.instructor}
              value={userId}
              disabled={
                pending ||
                result.state === "unknown" ||
                result.state === "partial"
              }
              onChange={(event) => {
                sequence.current++;
                setUserId(event.target.value);
                setExisting(null);
                setPhase("unread");
                setResult({ state: "none" });
              }}
            >
              <option value="">{words.chooseAnInstructor}</option>
              {instructors.data
                ?.filter((person) => person.user_id && person.account_is_active)
                .map((person) => (
                  <option key={person.id} value={person.user_id!}>
                    {person.display_name ?? words.unnamedInstructor}
                  </option>
                ))}
            </NativeSelect>
            <button
              type="button"
              data-act="check"
              className="min-h-10 justify-self-start text-primary underline"
              disabled={
                !userId ||
                phase === "reading" ||
                pending ||
                result.state === "unknown"
              }
              onClick={() => void inspect()}
            >
              {words.checkExistingVerification}
            </button>
            {phase === "reading" && (
              <p role="status">{words.readingVerification}</p>
            )}
            {phase === "known" && existing && (
              <dl className="grid gap-2 text-ui">
                <div>
                  <dt>{words.connection}</dt>
                  <dd>
                    {existing.connection.enabled
                      ? words.enabled
                      : words.disabled}
                    {words.verified}{" "}
                    {settingsText.verified(existing.connection.verified_at).replace(/^Verified /, "")}
                  </dd>
                </div>
                <div>
                  <dt>{words.zoomHost}</dt>
                  <dd>
                    {words.license} {settingsText.zoomLicense(existing.host.license_state)}
                    {words.verified}{" "}
                    {settingsText.verified(existing.host.verified_at).replace(/^Verified /, "")}
                  </dd>
                </div>
              </dl>
            )}


          </div>
        ),
      },
    ],
    commit: {
      verb: words.refreshZoomVerification,
      cap: words.whenYouRefreshVerification,
      effects: () => [
        {
          id: "connection",
          sentence: words.refreshTheExistingConnectionObservation,
        },
        {
          id: "host",
          sentence: words.refreshThisInstructorSExistingHostObservation,
        },
      ],
      blockers: () =>
        existing && phase === "known"
          ? []
          : [
              {
                kind: "missing",
                part: "verification",
                sentence: phase === "empty"
                  ? words.noMatchingActiveConnectionAndHostBindingWereFoundNothingCanBeRefreshed
                  : phase === "failed"
                    ? words.checkInstructorAgain
                    : words.readAnExistingConnectionAndHostBindingBeforeRefreshing,
              },
            ],
      run: (key) =>
        run(
          key,
          {
            zoom_user_id: existing!.host.zoom_user_id,
            connection_version: existing!.connection.verified_at,
            host_version: existing!.host.verified_at,
            ids: [
              `connection:${existing!.connection.account_id}`,
              `host:${existing!.host.host_binding_id}`,
            ],
          },
          userId,
        ),
    },
  };
}
